import type { SiteControllerState } from '../../lib/site-controllers';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertTriangle, CircleHelp, CircleCheck, WifiOff } from 'lucide-react';

export type ControllerStateInput = {
  state?: SiteControllerState;
  stateDescription?: string | null;
};

function aggregateWarningTooltip(controllers: ControllerStateInput[]): string | undefined {
  const lines = controllers
    .filter((c) => c.state === 'warning' && c.stateDescription?.trim())
    .map((c) => c.stateDescription!.trim());
  if (lines.length === 0) return undefined;
  return lines.join('\n\n');
}

/** Visual tone for border/text/icon (outline-style chips). */
export type AggregateTone = 'online' | 'warning' | 'offline' | 'neutral';

function getControllerAggregateStatus(controllers: ControllerStateInput[]): {
  label: string;
  tone: AggregateTone;
} {
  const count = controllers.length;
  const noControllers = count === 0;
  const allOnline = controllers.every((c) => c.state === 'online');
  const anyOffline = controllers.some((c) => c.state === 'offline');
  const allOffline = controllers.every((c) => c.state === 'offline');
  const anyWarning = controllers.some((c) => c.state === 'warning');

  const label = noControllers
    ? 'No Controllers'
    : allOnline
      ? 'Online'
      : allOffline
        ? 'Offline'
        : anyOffline
          ? 'Some Offline'
          : anyWarning
            ? 'Warning'
            : 'Status Unknown';

  const tone: AggregateTone = noControllers
    ? 'neutral'
    : allOnline
      ? 'online'
      : anyOffline
        ? 'offline'
        : anyWarning
          ? 'warning'
          : 'neutral';

  return { label, tone };
}

function getSingleControllerDisplay(input: ControllerStateInput): {
  label: string;
  tone: AggregateTone;
} {
  const s = input.state;
  if (s === 'online') return { label: 'Online', tone: 'online' };
  if (s === 'warning') return { label: 'Warning', tone: 'warning' };
  return { label: 'Offline', tone: 'offline' };
}

const toneClassName: Record<AggregateTone, string> = {
  online:
    'border-green-600/55 bg-green-500/12 text-green-800 shadow-none hover:bg-green-500/15 dark:border-green-500/45 dark:bg-green-500/12 dark:text-green-300',
  warning:
    'border-amber-500/60 bg-amber-500/12 text-amber-500 shadow-none hover:bg-amber-500/15 dark:border-amber-500/45 dark:bg-amber-500/12 dark:text-amber-200',
  offline:
    'border-red-600/55 bg-red-500/12 text-red-800 shadow-none hover:bg-red-500/15 dark:border-red-500/45 dark:bg-red-500/12 dark:text-red-300',
  neutral: '',
};

function AggregateIcon({ tone }: { tone: AggregateTone }) {
  const iconClass = 'h-3.5 w-3.5 shrink-0';
  switch (tone) {
    case 'online':
      return <CircleCheck className={cn(iconClass, 'text-green-600 dark:text-green-400')} aria-hidden />;
    case 'warning':
      return <AlertTriangle className={cn(iconClass, 'text-amber-600 dark:text-amber-400')} aria-hidden />;
    case 'offline':
      return <WifiOff className={cn(iconClass, 'text-red-600 dark:text-red-400')} aria-hidden />;
    default:
      return <CircleHelp className={cn(iconClass, 'text-muted-foreground')} aria-hidden />;
  }
}

interface ControllerStateBadgeProps {
  /** Aggregate status for a site (dashboard, sites list). Omit when using `controller`. */
  controllers?: ControllerStateInput[];
  /** One controller row — labels Online / Warning / Offline. Omit when using `controllers`. */
  controller?: ControllerStateInput;
  className?: string;
  /** When set, wraps the badge (e.g. layout in table cells). */
  wrapperClassName?: string;
}

export default function SiteControllerStateBadge({
  controllers = [],
  controller,
  className,
  wrapperClassName,
}: ControllerStateBadgeProps) {
  const { label, tone } =
    controller !== undefined
      ? getSingleControllerDisplay(controller)
      : getControllerAggregateStatus(controllers);
  const isColored = tone !== 'neutral';

  const tooltipText =
    controller !== undefined
      ? controller.state === 'warning' && controller.stateDescription?.trim()
        ? controller.stateDescription.trim()
        : undefined
      : tone === 'warning'
        ? aggregateWarningTooltip(controllers)
        : undefined;

  const badge = (
    <Badge
      className={cn(
        'inline-flex items-center gap-1.5 font-medium',
        isColored ? cn('border', toneClassName[tone]) : null,
        className,
      )}
      variant={isColored ? 'outline' : 'secondary'}
    >
      <AggregateIcon tone={tone} />
      {label}
    </Badge>
  );

  const trigger = (
    <span
      className={cn(
        'inline-flex max-w-full cursor-default',
        wrapperClassName,
      )}
    >
      {badge}
    </span>
  );

  if (!tooltipText) {
    return trigger;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-sm whitespace-pre-wrap text-left font-normal">
        {tooltipText}
      </TooltipContent>
    </Tooltip>
  );
}
